/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography, useTheme, useMediaQuery, ToggleButtonGroup } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GetStoreOverviewLayout,
  UpdateBayCoordinates,
  DeleteBays,
  GetAllCategories,
  GetAllFixtures,
  CreateNewBays,
  UploadLayoutImageUrl,
  CreateStoreConfig
} from '../../../api/index';
import { useSelector } from 'react-redux';
import Draggable from 'react-draggable';
import CircleIcon from '@mui/icons-material/Circle';
import SquareIcon from '@mui/icons-material/Square';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import DiamondIcon from '@mui/icons-material/Diamond';
import RectangleIcon from '@mui/icons-material/Rectangle';
import StarIcon from '@mui/icons-material/Star';
import HexagonIcon from '@mui/icons-material/Hexagon';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import AddIcon from '@mui/icons-material/Add';
import PentagonIcon from '@mui/icons-material/Pentagon';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveIcon from '@mui/icons-material/Remove';
import ModelViewer from './ModelViewer';

const fixtureIconMap = {
  'Wall Bay': <CircleIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Impulse Fixture': <SquareIcon sx={{ fontSize: 16, color: 'white' }} />,
  'A Rail': <ChangeHistoryIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Long Table': <DiamondIcon sx={{ fontSize: 16, color: 'white' }} />,
  'H Gandola': <RectangleIcon sx={{ fontSize: 16, color: 'white' }} />,
  '4 Way': <HexagonIcon sx={{ fontSize: 16, color: 'white' }} />,
  'Nested Table': <StarIcon sx={{ fontSize: 16, color: 'white' }} />,
  'U Bar': <PanoramaFishEyeIcon sx={{ fontSize: 16, color: 'white' }} />,
  Gondola: <PentagonIcon sx={{ fontSize: 16, color: 'white' }} />,
  mannequine: <AddIcon sx={{ fontSize: 16, color: 'white' }} />
};

const StoreOverviewLayout = () => {
  const { store } = useParams();
  const [layoutData, setLayoutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [bays, setBays] = useState([]);
  const [originalBays, setOriginalBays] = useState([]);
  const imageRef = useRef(null);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedBays, setSelectedBays] = useState([]);
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const [addingBay, setAddingBay] = useState(false);
  const [newBayCoords, setNewBayCoords] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedFixture, setSelectedFixture] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFixtureInfo] = useState(true);
  const [selectedFixtureTypes, setSelectedFixtureTypes] = useState([]);
  const [infoPanelOpen] = useState(true);
  const [panelMinimized, setPanelMinimized] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [uploadingImage, setUploadingImage] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [hasUploadedNewImage, setHasUploadedNewImage] = useState(false);
  const [previewLayoutImage, setPreviewLayoutImage] = useState(null);
  const [threeD, setThreeD] = useState('2D');

  const navigate = useNavigate();

  const selectedDate = useSelector((state) => state.customization.selectedDate)
    .toISOString()
    .slice(0, 10);

  const getStoreOverviewData = async () => {
    setLoading(true);
    try {
      const response = await GetStoreOverviewLayout({
        dates: selectedDate,
        store_id: store
      });
      if (response && response.data) {
        setLayoutData(response.data);
        const allBays = response.data.data[0]?.store_config?.flatMap((category) => category.bays) || [];
        setBays(allBays);
        setOriginalBays(allBays);
      } else {
        console.error('Error: No data available');
      }
    } catch (error) {
      console.error('Error fetching store overview layout:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStoreOverviewData();
    fetchFixtureAndCategories();
  }, [store, selectedDate]);

  const handleBayClick = (bayId) => {
    if (!editing) {
      navigate(`/main/stores/bay-details/${bayId}?storeId=${store}`);
    }
  };

  const handleBackToStores = () => {
    navigate('/main/stores');
  };

  const handleDragStop = (e, data, bayId) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const newXPercent = (data.x / rect.width) * 100;
    const newYPercent = (data.y / rect.height) * 100;

    setBays((prevBays) =>
      prevBays.map((bay) =>
        bay._id === bayId
          ? { ...bay, coordinates: { x: Math.min(Math.max(newXPercent, 0), 100), y: Math.min(Math.max(newYPercent, 0), 100) } }
          : bay
      )
    );
  };

  const handleSave = async () => {
    if (hasUploadedNewImage) {
      // New image uploaded - save complete config
      await handleSaveNewConfig();
    } else {
      // Existing flow for coordinate updates only
      const updatedBays = bays.filter((bay) => {
        const originalBay = originalBays.find((ob) => ob._id === bay._id);
        if (!originalBay) return false;
        return originalBay.coordinates?.x !== bay.coordinates?.x || originalBay.coordinates?.y !== bay.coordinates?.y;
      });

      const payload = updatedBays.map(({ _id, coordinates }) => ({ _id, coordinates }));
      if (payload.length > 0) {
        try {
          const response = await UpdateBayCoordinates(payload);
          console.log('UpdateBayCoordinates API success:', response);
        } catch (error) {
          console.error('Error updating bay coordinates:', error);
        }
      }
    }

    setEditing(false);
    setHasUploadedNewImage(false);
  };

  const toggleBaySelectForDelete = (bayId) => {
    setSelectedBays((prev) => (prev.includes(bayId) ? prev.filter((id) => id !== bayId) : [...prev, bayId]));
  };

  const handleConfirmDelete = async () => {
    try {
      const payload = selectedBays.map((id) => ({ _id: id }));
      await DeleteBays(payload);
      setDeletePopupOpen(false);
      setSelectedBays([]);
      setDeleteMode(false);
      getStoreOverviewData();
    } catch (error) {
      console.error('Error deleting bays:', error);
    }
  };

  const UploadToBlob = async (sasUrl, file) => {
    console.log('UploadToBlob called with:', { sasUrl, fileName: file.name, fileType: file.type });

    try {
      const response = await axios.put(sasUrl, file, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type
        }
      });

      console.log('Blob upload successful:', response.status);
      return response;
    } catch (error) {
      console.error('UploadToBlob error:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);

    const previewUrl = URL.createObjectURL(file);
    setPreviewLayoutImage(previewUrl);

    if (!originalImage && layoutData?.data[0]?.layout_image) {
      setOriginalImage(layoutData.data[0].layout_image);
    }

    setHasUploadedNewImage(true);
    setEditing(true);
    event.target.value = ''; // clear file input

    try {
      const urlResponse = await UploadLayoutImageUrl({});
      const signedUrl = urlResponse?.data?.signed_url;
      if (!signedUrl) throw new Error('No signed URL returned');

      await UploadToBlob(signedUrl, file);

      const finalImageUrl = signedUrl.split('?')[0];

      // ✅ Do NOT set this yet — show preview until user saves config
      setLayoutData((prev) => ({
        ...prev,
        data: [
          {
            ...prev.data[0],
            layout_image: finalImageUrl
          }
        ]
      }));

      // setPreviewLayoutImage(null);
    } catch (error) {
      console.error('Upload failed:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveNewConfig = async () => {
    try {
      // Get unique categories from current bays
      // const uniqueCategories = [];
      const categoryMap = new Map();

      // Extract categories from the current layout data
      if (layoutData?.data[0]?.store_config) {
        layoutData.data[0].store_config.forEach((category) => {
          if (!categoryMap.has(category.category_id)) {
            categoryMap.set(category.category_id, {
              category_id: category.category_id,
              category_name: category.category_name,
              coordinates: [
                {
                  x1: 0,
                  y1: 0,
                  x2: 100,
                  y2: 100
                }
              ]
            });
          }
        });
      }

      // Convert map to array
      const layoutArray = Array.from(categoryMap.values());

      const payload = {
        store_oid: store,
        layout_image: layoutData.data[0].layout_image,
        layout: layoutArray
      };

      console.log('CreateStoreConfig payload:', payload);

      const response = await CreateStoreConfig(payload);
      console.log('CreateStoreConfig API success:', response);

      await getStoreOverviewData();
      setEditing(false);
    } catch (error) {
      console.error('Error creating store config:', error);
      alert('Failed to save store configuration. Please try again.');
    }
  };

  const renderDeletePopup = () => (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        maxHeight: 420,
        backgroundColor: '#fff',
        boxShadow: 24,
        p: 3,
        borderRadius: 2,
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography variant="h6" gutterBottom>
        Confirm Bay Deletion
      </Typography>

      {/* Scrollable bay list */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          border: '1px solid #eee',
          p: 1,
          borderRadius: 1,
          maxHeight: 250
        }}
      >
        {selectedBays.map((id) => {
          const bay = bays.find((b) => b._id === id);
          return (
            <Box
              key={id}
              sx={{
                mb: 1,
                p: 2,
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: '#fdecea', // light red
                border: '1px solid #f44336',
                '&:hover': {
                  backgroundColor: '#f8d7da' // slightly darker on hover
                }
              }}
            >
              <Typography>
                <strong>Bay ID:</strong> {bay?.bay_id || id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {bay?.fixture_name} - {bay?.type}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Buttons - fixed at bottom */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setDeletePopupOpen(false);
            setSelectedBays([]);
          }}
        >
          Cancel
        </Button>

        <Button
          variant="outlined"
          color="error"
          sx={{
            backgroundColor: '#FD5E86',
            color: '#000',
            '&:hover': {
              backgroundColor: '#FB1751'
            }
          }}
          onClick={handleConfirmDelete}
        >
          Delete
        </Button>
      </Box>
    </Box>
  );

  const fetchFixtureAndCategories = async () => {
    try {
      const [fixtureRes, categoryRes] = await Promise.all([GetAllFixtures({}), GetAllCategories({})]);

      console.log('fixtureRes', fixtureRes.data);
      console.log('categoryRes', categoryRes.data);
      setFixtures(fixtureRes?.data.data || []);
      setCategories(categoryRes?.data.data || []);
    } catch (err) {
      console.error('Error fetching fixtures or categories', err);
    }
  };

  const handleLayoutClick = (e) => {
    if (!addingBay || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const xPercent = ((e.clientX - rect.left) / rect.width) * 100;
    const yPercent = ((e.clientY - rect.top) / rect.height) * 100;

    setNewBayCoords({ x: xPercent, y: yPercent });
  };

  const renderAddBayPopup = () => (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        backgroundColor: '#fff',
        boxShadow: 24,
        p: 3,
        borderRadius: 2,
        zIndex: 1300
      }}
    >
      <Typography variant="h6" gutterBottom>
        Add New Bay
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Selected Coordinates: {newBayCoords ? `X: ${newBayCoords.x.toFixed(2)}%, Y: ${newBayCoords.y.toFixed(2)}%` : 'Click on layout'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Select Fixture
          </Typography>
          <select
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            value={selectedFixture}
            onChange={(e) => setSelectedFixture(e.target.value)}
          >
            <option value="">Select Fixture</option>
            {fixtures.map((f) => (
              <option key={f._id} value={f._id}>
                {f.name}
              </option>
            ))}
          </select>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Select Category
          </Typography>
          <select
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Select Category</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => {
            setAddingBay(false);
            setNewBayCoords(null);
            setSelectedCategory('');
            setSelectedFixture('');
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!newBayCoords || !selectedCategory || !selectedFixture}
          onClick={async () => {
            const payload = [
              {
                coordinates: newBayCoords,
                fixture_oid: selectedFixture,
                category_oid: selectedCategory,
                store_oid: store
              }
            ];
            try {
              await CreateNewBays(payload);
              setAddingBay(false);
              setNewBayCoords(null);
              setSelectedCategory('');
              setSelectedFixture('');
              getStoreOverviewData();
            } catch (err) {
              console.error('Failed to create bay:', err);
            }
          }}
        >
          Create Bay
        </Button>
      </Box>
    </Box>
  );

  const toggleFixtureType = (fixtureName) => {
    setSelectedFixtureTypes((prev) => (prev.includes(fixtureName) ? prev.filter((f) => f !== fixtureName) : [...prev, fixtureName]));
  };

  const handle3D = (e) => {
    console.log('toggle value is', e.target.value);
    if (e.target.value) {
      setThreeD(e.target.value);
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          mb: 4,
          gap: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'stretch' : 'center',
            width: '100%',
            gap: 2
          }}
        >
          <Button
            variant="contained"
            onClick={handleBackToStores}
            sx={{
              backgroundColor: '#2e7d32',
              color: 'black',
              '&:hover': { backgroundColor: '#1b5e20', color: 'white' }
            }}
          >
            Back to Stores
          </Button>
          <Typography variant="h4" sx={{ fontSize: isMobile ? '1.5rem' : '2.125rem' }}>
            Store Overview: {layoutData?.data[0]?.store_id}
          </Typography>
          <Box
            sx={{
              ml: isMobile ? 0 : 'auto',
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
              mt: isMobile ? 2 : 0,
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => document.getElementById('layout-image-upload').click()}
              disabled={uploadingImage}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                py: isMobile ? 0.5 : 1
              }}
            >
              {uploadingImage ? 'Uploading...' : 'Upload Layout'}
            </Button>

            {hasUploadedNewImage && (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  setLayoutData((prev) => ({
                    ...prev,
                    data: [
                      {
                        ...prev.data[0],
                        layout_image: originalImage
                      }
                    ]
                  }));
                  setHasUploadedNewImage(false);
                }}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  py: isMobile ? 0.5 : 1
                }}
              >
                Reset to Original
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setAddingBay(!addingBay);
                setNewBayCoords(null);
              }}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                py: isMobile ? 0.5 : 1
              }}
            >
              {addingBay ? 'Cancel Add Bay' : 'Add Bay'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setEditing(!editing)}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
              disabled={!layoutData?.data[0]?.layout_image} // Enable only when image exists
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                py: isMobile ? 0.5 : 1
              }}
            >
              {editing ? 'Cancel' : 'Edit Bays'}
            </Button>
            {editing && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  py: isMobile ? 0.5 : 1
                }}
              >
                Save
              </Button>
            )}

            {/* toggle for 3D View  */}
            <ToggleButtonGroup color="primary" value={threeD} exclusive onChange={handle3D} aria-label="Platform">
              {/* <ToggleButton value="2D">2D</ToggleButton> */}
              {/* <ToggleButton value="3D">3D</ToggleButton> */}
              {/* <ToggleButton value="ios">iOS</ToggleButton> */}
            </ToggleButtonGroup>

            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setDeleteMode(!deleteMode);
                setSelectedBays([]);
              }}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                py: isMobile ? 0.5 : 1
              }}
            >
              {deleteMode ? 'Cancel Delete' : 'Delete Bays'}
            </Button>
            {deleteMode && selectedBays.length > 0 && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setDeletePopupOpen(true)}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  py: isMobile ? 0.5 : 1
                }}
              >
                Confirm Delete ({selectedBays.length})
              </Button>
            )}
          </Box>
          <input id="layout-image-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
        </Box>
        <Typography variant="subtitle1" color="text.secondary">
          Date: {selectedDate}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      ) : layoutData?.data && layoutData?.data[0]?.layout_image ? (
        <Box sx={{ position: 'relative', width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
          {threeD == '2D' ? (
            <div
              onClick={handleLayoutClick}
              style={{
                position: 'relative',
                width: '100%',
                height: 'auto',
                display: 'inline-block',
                touchAction: 'none' // Prevent default touch actions on mobile
              }}
            >
              <img
                ref={imageRef}
                src={previewLayoutImage || layoutData?.data[0]?.layout_image}
                alt="Store Layout"
                style={{ display: 'block', width: '100%', height: 'auto' }}
              />

              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                {bays
                  .filter((bay) => selectedFixtureTypes.length === 0 || selectedFixtureTypes.includes(bay.fixture_name))
                  .map((bay) => {
                    const topPercent = bay.coordinates?.y || 0;
                    const leftPercent = bay.coordinates?.x || 0;

                    const rect = imageRef.current?.getBoundingClientRect();
                    const defaultX = rect ? (leftPercent * rect.width) / 100 : 0;
                    const defaultY = rect ? (topPercent * rect.height) / 100 : 0;

                    const icon = fixtureIconMap[bay.fixture_name] || <SquareIcon sx={{ fontSize: 18, color: 'white' }} />;

                    const button = (
                      <Tooltip
                        title={
                          showFixtureInfo ? (
                            <div style={{ padding: '4px 8px' }}>
                              <div>
                                <strong>Fixture:</strong> {bay.fixture_name || 'N/A'}
                              </div>
                              <div>
                                <strong>Type:</strong> {bay.type ? bay.type.charAt(0).toUpperCase() + bay.type.slice(1) : 'N/A'}
                              </div>
                              {/* {bay.hasCapture && Array.isArray(bay.captureDetails) && bay.captureDetails.length > 0 && (
                              <div>
                                <strong>OSA:</strong>{' '}
                                {(() => {
                                  const total = bay.captureDetails.reduce((sum, item) => sum + (item.fullness || 0), 0);
                                  const avg = total / bay.captureDetails.length;
                                  return `${avg.toFixed(1)}%`;
                                })()}
                              </div>
                            )} */}
                            </div>
                          ) : (
                            ''
                          )
                        }
                        arrow
                        placement="bottom"
                      >
                        <Button
                          variant={deleteMode && selectedBays.includes(bay._id) ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => (deleteMode ? toggleBaySelectForDelete(bay._id) : handleBayClick(bay._id))}
                          style={{
                            backgroundColor: deleteMode
                              ? selectedBays.includes(bay._id)
                                ? '#e53935'
                                : '#f5f5f5'
                              : bay.hasCapture
                              ? (() => {
                                  const details = bay.captureDetails ?? [];
                                  const totalFullness = details.reduce((sum, item) => sum + (item.fullness ?? 0), 0);
                                  const avgFullness = details.length > 0 ? totalFullness / details.length : 0;

                                  if (avgFullness > 90) return '#4caf50'; // Green
                                  if (avgFullness >= 50) return '#ffb300'; // Amber
                                  return '#e53935'; // Red
                                })()
                              : '#9e9e9e', // gray when no capture
                            minWidth: isMobile ? '20px' : '30px',
                            width: isMobile ? '20px' : '30px',
                            height: isMobile ? '20px' : '30px',
                            borderRadius: '50%',
                            padding: 0,
                            border: '2px solid white',
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
                            transition: 'all 0.3s ease',
                            cursor: deleteMode ? 'pointer' : editing ? 'move' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {icon}
                        </Button>
                      </Tooltip>
                    );

                    return editing ? (
                      <Draggable
                        key={bay._id}
                        defaultPosition={{ x: defaultX, y: defaultY }}
                        onStop={(e, data) => handleDragStop(e, data, bay._id)}
                        bounds="parent"
                      >
                        <div style={{ position: 'absolute' }}>{button}</div>
                      </Draggable>
                    ) : (
                      <div
                        key={bay._id}
                        style={{
                          position: 'absolute',
                          top: `${topPercent}%`,
                          left: `${leftPercent}%`,
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                          pointerEvents: 'auto'
                        }}
                      >
                        {button}
                      </div>
                    );
                  })}

                {addingBay && newBayCoords && (
                  <div
                    style={{
                      position: 'absolute',
                      top: `${newBayCoords.y}%`,
                      left: `${newBayCoords.x}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 5
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      disabled
                      style={{
                        backgroundColor: '#00c853',
                        minWidth: '15px',
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        padding: 0,
                        border: '2px solid white',
                        opacity: 0.8,
                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : layoutData?.data && layoutData?.data[0]?.digitalTwinImg ? (
            <ModelViewer modelUrl={layoutData.data[0].digitalTwinImg} />
          ) : (
            <Box sx={{ mt: 3, p: 4, border: '1px solid #ddd', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No 3D model available for this store
              </Typography>
            </Box>
          )}
          {newBayCoords && addingBay && renderAddBayPopup()}
          {deletePopupOpen && renderDeletePopup()}
          {infoPanelOpen && panelMinimized && (
            <IconButton
              onClick={() => setPanelMinimized(false)}
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                zIndex: 5,
                bgcolor: 'white',
                border: '1px solid #ccc',
                boxShadow: 3,
                width: 40,
                height: 40
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          )}

          {infoPanelOpen && !panelMinimized && (
            <Draggable handle=".drag-handle" bounds="parent" disabled={isMobile}>
              <Box
                sx={{
                  position: isMobile ? 'fixed' : 'absolute',
                  top: isMobile ? 'auto' : 5,
                  bottom: isMobile ? 0 : 'auto',
                  right: isMobile ? 0 : 5,
                  left: isMobile ? 0 : 'auto',
                  width: isMobile ? '100%' : 260,
                  bgcolor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: isMobile ? '16px 16px 0 0' : 2,
                  boxShadow: 3,
                  p: 2,
                  zIndex: 1000,
                  maxHeight: isMobile ? '60vh' : 'none',
                  overflowY: 'auto'
                }}
              >
                <Box
                  className="drag-handle"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1,
                    cursor: 'move',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1
                    }
                  }}
                >
                  <Typography variant="subtitle1">Fixture Info</Typography>
                  <Box>
                    <IconButton size="small" onClick={() => setPanelMinimized(true)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    {/* <IconButton size="small" onClick={() => setInfoPanelOpen(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton> */}
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  Select one or more fixture types to filter.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {fixtures.map((fixture) => (
                    <Tooltip title={fixture.name} key={fixture._id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.6,
                          borderRadius: '10%',
                          bgcolor: selectedFixtureTypes.includes(fixture.name) ? '#1976d2' : '#f5f5f5',
                          color: selectedFixtureTypes.includes(fixture.name) ? 'white' : 'black',
                          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: selectedFixtureTypes.includes(fixture.name) ? '#1565c0' : '#ddd'
                          }
                        }}
                        onClick={() => toggleFixtureType(fixture.name)}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            backgroundColor: '#9E9E9E',
                            border: '2px solid #fff',
                            color: 'white'
                          }}
                        >
                          {fixtureIconMap[fixture.name] || <SquareIcon fontSize="small" />}
                        </Box>
                        <Typography variant="caption" sx={{ color: 'black' }}>
                          {fixture.name}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>

                <Button variant="outlined" size="small" onClick={() => setSelectedFixtureTypes([])} fullWidth>
                  Clear Filters
                </Button>
              </Box>
            </Draggable>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: 3, p: 4, border: '1px solid #ddd', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No images available for this store
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default StoreOverviewLayout;
